import { computed, Inject, Injectable, Signal } from '@angular/core';
import { map, tap, Observable } from 'rxjs';
import { RisksCatalogStore } from '../state/risks-catalog.store';
import { RiskCatalog, RiskCatalogRequest } from '../../api/models/risks/risks.interface';
import { FeatureFlagQuery } from 'src/app/feature-flag/state/feature-flag.query';
import { FeatureFlags } from 'src/app/api/models/profile/profile-feature-flags.enum';
import { SanitizationService } from '../../shared/services/sanitization.service';
import { API_CLIENT_RISKS_SERVICE } from 'src/app/api/injectors/api-client-risks.token';
import { BaseApiClientRisksService } from 'src/app/api/base-api-client-risks.service';
import { AssessmentEditOpEnum, AssessmentTemplateEnum } from 'src/app/api/models/assessments/assessment.enum';
import { NormalizeKeyPipe } from 'src/app/shared/pipes/normalize-key.pipe';

@Injectable({
     providedIn: 'root'
})
export class RisksCatalogService {

    constructor(
        private sanitizationService: SanitizationService,
        private store: RisksCatalogStore,
        private normalizeKeyPipe: NormalizeKeyPipe,
        @Inject(API_CLIENT_RISKS_SERVICE) private apiClientRisksService: BaseApiClientRisksService,
        private featureFlagQuery: FeatureFlagQuery,
    ) { }

    getEmpty(): Signal<boolean | undefined> {
        return this.store.signal('empty');
    }

    setLoading(loading: boolean): void {
        this.store.set({ 'loading': loading });
    }
        
    setDisabled(disabled: boolean): void {
        this.store.set({ 'disabled': disabled });
    }

    getLoading(): Signal<boolean | undefined> {
        return this.store.signal('loading');
    }
        
    selectLoading(): Observable<boolean | undefined> {
        return this.store.select('loading');
    }
        
    getDisabled(): Signal<boolean | undefined> {
        return this.store.signal('disabled');
    }
        
    selectDisabled(): Observable<boolean | undefined> {
        return this.store.select('disabled');
    }

    getRisksCatalog(asArray: boolean = false): Signal<Map<string, RiskCatalog> | RiskCatalog[]> {
        const risksCatalogSignal = this.store.signal('risksCatalog');
        if (asArray) {
            return computed(() => Array.from(new Set([...risksCatalogSignal().values()]))) as Signal<RiskCatalog[]>;
        }
        return risksCatalogSignal;
    }

    selectRisksCatalog(asArray: boolean = false): Observable<Map<string, RiskCatalog> | RiskCatalog[]> {
        if (asArray) {
            return this.store.select('risksCatalog').pipe(
                map(risksCatalog => Array.from(new Set([...risksCatalog.values()])))
            );
        }
        return this.store.select('risksCatalog');
    }

    getRiskCatalogItemByType(type: string) {
        return this.store.get('risksCatalog').get(type);
    }

    getCatalogRisksByType(): Signal<Map<AssessmentTemplateEnum, { key: string, value: string, isDefault: boolean }[]>> {
        return computed(() => {
            // Initialize the mapped risks with all template types
            const mappedRisks = new Map<AssessmentTemplateEnum, { key: string, value: string, isDefault: boolean; }[]>();
    
            Object.values(AssessmentTemplateEnum).forEach(templateType => {
                mappedRisks.set(this.normalizeKeyPipe.transform(templateType) as AssessmentTemplateEnum, []);
            });
    
            // Retrieve risks as an array from getRisksCatalog
            const riskCatalogs = this.getRisksCatalog(true)() as RiskCatalog[];
    
            // Iterate through each risk catalog and map it to the corresponding template type
            riskCatalogs
                .filter(riskCatalog => !riskCatalog.isHidden)  // Filter out hidden risks
                .forEach(riskCatalog => {
                    const { type, name, assessmentTypes, isDefault } = riskCatalog;
                    const riskItem = { key: type, value: name, isDefault };
                    if (assessmentTypes.length === 0) {
                        // If assessmentTypes is empty, map it to all template types
                        mappedRisks.forEach((_, templateType) => {
                            mappedRisks.get(this.normalizeKeyPipe.transform(templateType) as AssessmentTemplateEnum)?.push(riskItem);
                        });
                    } else {
                        // Otherwise, map to specific assessment types
                        assessmentTypes.forEach(templateType => {
                            mappedRisks.get(this.normalizeKeyPipe.transform(templateType) as AssessmentTemplateEnum)?.push(riskItem);
                        });
                    }
                });
    
            return mappedRisks;
        });
    }

    isCustomRiskAlreadyExist(name: string): boolean {
        const risksCatalog = this.getRisksCatalog(true)() as RiskCatalog[];
        const sanitizedName = this.sanitizationService.sanitizeText(name?.trim());
        return !!risksCatalog.find(risk => {
            const sanitizedRiskName = this.sanitizationService.sanitizeText(risk.name?.trim());
            return sanitizedRiskName === sanitizedName;
        });
    }

    createRiskCatalog(riskCatalog: RiskCatalog): Observable<RiskCatalog> {
        return this.apiClientRisksService.createRiskCatalog(this.toRequest(riskCatalog)).pipe(
            map(savedCatalog => this.mergeSaved(riskCatalog, savedCatalog)),
            tap((savedCatalog) => {
              this.store.addRiskCatalog(savedCatalog);
            })
        );
    }

    updateRiskCatalogByType(type: string, riskCatalog: RiskCatalog): Observable<RiskCatalog> {
        return this.apiClientRisksService.updateRiskCatalogByType(type, this.toRequest(riskCatalog)).pipe(
            map(savedCatalog => this.mergeSaved(riskCatalog, savedCatalog)),
            tap((savedCatalog) => this.store.updateRiskCatalog(savedCatalog))
        );
    }

    /**
     * Hide / unhide a catalog risk. DevRiskLabels: an existing row is JSON-patched (`/isHidden` only, so its tags are
     * untouched); a predefined risk the company never edited has no row yet (`id` null) and still goes through the
     * PUT by type that creates it. Flag off: the PUT by type, as before.
     */
    toggleRiskCatalogVisibility(riskCatalog: RiskCatalog): Observable<RiskCatalog> {
        const updated: RiskCatalog = { ...riskCatalog, isHidden: !riskCatalog.isHidden };
        const hasRow = riskCatalog.id !== null && riskCatalog.id !== undefined;
        if (!this.featureFlagQuery.getFlag(FeatureFlags.DevRiskLabels) || !hasRow) {
            return this.updateRiskCatalogByType(riskCatalog.type, updated);
        }
        return this.apiClientRisksService.patchRiskCatalog(riskCatalog.id, [
            { op: AssessmentEditOpEnum.Replace, path: '/isHidden', value: updated.isHidden },
        ]).pipe(
            map(savedCatalog => this.mergeSaved(updated, savedCatalog)),
            tap(savedCatalog => this.store.updateRiskCatalog(savedCatalog))
        );
    }

    updateRiskCatalog(id: number, riskCatalog: RiskCatalog): Observable<RiskCatalog> {
        return this.apiClientRisksService.updateRiskCatalog(id, this.toRequest(riskCatalog)).pipe(
            map(savedCatalog => this.mergeSaved(riskCatalog, savedCatalog)),
            tap((savedCatalog) => this.store.updateRiskCatalog(savedCatalog))
        );
    }

    /**
     * DevRiskLabels write contract: `tags` go out as `tagIds` (whole list, every call — hide-only edits included)
     * and `description` only for custom risks (the BE rejects it on predefined ones). Flag off → legacy payload.
     */
    private toRequest(riskCatalog: RiskCatalog): RiskCatalogRequest {
        const { tags, description, ...rest } = riskCatalog;
        if (!this.featureFlagQuery.getFlag(FeatureFlags.DevRiskLabels)) {
            return rest;
        }
        return {
            ...rest,
            tagIds: (tags ?? []).map(tag => tag.id).filter(id => id !== null && id !== undefined),
            ...(riskCatalog.isDefault ? {} : { description: description ?? '' }),
        };
    }

    /** Keeps labels/description in the store even while the BE response doesn't echo them yet. */
    private mergeSaved(sent: RiskCatalog, saved: RiskCatalog): RiskCatalog {
        return { ...sent, ...saved, tags: saved?.tags ?? sent.tags, description: saved?.description ?? sent.description };
    }

    deleteRiskCatalog(id: number, type: string): Observable<void> {
        return this.apiClientRisksService.deleteRiskCatalog(id).pipe(
            tap(() => this.store.deleteRiskCatalog(type))
        );
    }
}